import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {getHasNewIntegrationsDiscoverabilityP4} from '../../../../store/featureFlags/featureFlagsSelectors';
import {DraggableSwitcherItem, DraggableSwitcherItemPublicProps} from './draggableSwitcherItem';

/*
 * Props.
 */

interface EntryPointSwitcherItemProps extends DraggableSwitcherItemPublicProps {
  name: string;
  color: PaletteColorsEnum;
  iconBaseUrl?: string;
}

/*
 * Style.
 */

interface ColorWrapperStyleProps {
  $isDragging: boolean;
  $isNewIntDiscoveryEnabled: boolean;
  $isSelected?: boolean;
  $color: PaletteColorsEnum;
}

const StyledColorWrapperDiv = styled.div<ColorWrapperStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};

  ${(p) =>
    p.$isNewIntDiscoveryEnabled &&
    css`
      color: ${p.theme.palette[p.$color].shade40};
    `};
  ${(p) =>
    !p.$isNewIntDiscoveryEnabled &&
    css`
      color: ${p.theme.greys.shade60};
      ${addColorStyles(p)};
    `};
`;

function addColorStyles({$isDragging, $isSelected, $color}: ColorWrapperStyleProps) {
  if ($isDragging) {
    return css`
      color: ${(p) => p.theme.palette[$color].shade40};
    `;
  }

  if ($isSelected) {
    return css`
      color: ${(p) => p.theme.greys.white};
      background-color: ${(p) => p.theme.palette[$color].shade40};
    `;
  }

  return css`
    &:hover {
      color: ${(p) => p.theme.palette[$color].shade40};
    }
  `;
}

const StyledInitialDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
  margin-bottom: 1px;
`;
interface IconStyleProps {
  $isSelected: boolean;
}

const StyledIconDiv = styled.div<IconStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};
`;

const StyledDefaultIconDiv = styled(StyledIconDiv)`
  ${(p) => addDefaultStyle(p.$isSelected)}
`;

function addDefaultStyle(isSelected: boolean) {
  // Hide on selected.
  if (isSelected) {
    return css`
      display: none;
    `;
  }

  // Hide on hover.
  return css`
    *:hover > && {
      display: none;
    }
  `;
}

const StyledHoverIconDiv = styled(StyledIconDiv)`
  ${(p) => addHoverStyle(p.$isSelected)}
`;

function addHoverStyle(isSelected: boolean) {
  // Hide on selected.
  if (isSelected) {
    return css`
      display: none;
    `;
  }

  // Show on hover.
  return css`
    display: none;
    *:hover > && {
      display: flex;
    }
  `;
}

const StyledSelectedIconDiv = styled(StyledIconDiv)`
  /* Hide on non-selected. */
  ${(p) => !p.$isSelected && 'display: none;'}
`;

/*
 * Component.
 */

export const EntryPointSwitcherItem: FC<EntryPointSwitcherItemProps> = (props) => {
  const isNewIntDiscoveryEnabled = useAppSelector(getHasNewIntegrationsDiscoverabilityP4);
  return (
    <DraggableSwitcherItem
      {...props}
      renderTooltip={() => props.name}
      interactionId={maybeAddInteractionId(props.name)}
    >
      {(isDragging) =>
        props.iconBaseUrl
          ? renderIcon(props, isDragging, isNewIntDiscoveryEnabled)
          : renderColor(props, isDragging, isNewIntDiscoveryEnabled)
      }
    </DraggableSwitcherItem>
  );
};

// TODO: Remove interactionId logic when Jira and Asana rollouts finish.
function maybeAddInteractionId(name: string) {
  if (name === 'Asana') {
    return 'new-asana-plugin';
  }

  if (name === 'JIRA') {
    return 'new-jira-plugin';
  }

  return undefined;
}

function renderColor(
  props: EntryPointSwitcherItemProps,
  isDragging: boolean,
  isNewIntDiscoveryEnabled: boolean,
) {
  return (
    <StyledColorWrapperDiv
      $isDragging={isDragging}
      $isSelected={props.isSelected}
      $color={props.color}
      $isNewIntDiscoveryEnabled={isNewIntDiscoveryEnabled}
    >
      <StyledInitialDiv>{props.name[0] || 'P'}</StyledInitialDiv>
    </StyledColorWrapperDiv>
  );
}

function renderIcon(
  props: EntryPointSwitcherItemProps,
  isDragging: boolean,
  isNewIntDiscoveryEnabled: boolean,
) {
  if (isDragging) {
    return <img alt="integration-hover" src={buildIconUrl(props, 'hover')} />;
  }

  return (
    <>
      <StyledDefaultIconDiv $isSelected={Boolean(props.isSelected)}>
        <img
          alt="integration-default"
          src={buildIconUrl(props, isNewIntDiscoveryEnabled ? 'hover' : 'default')}
        />
      </StyledDefaultIconDiv>
      <StyledHoverIconDiv $isSelected={Boolean(props.isSelected)}>
        <img alt="integration-hover" src={buildIconUrl(props, 'hover')} />
      </StyledHoverIconDiv>
      <StyledSelectedIconDiv $isSelected={Boolean(props.isSelected)}>
        <img
          alt="integration-selected"
          src={buildIconUrl(props, isNewIntDiscoveryEnabled ? 'hover' : 'selected')}
        />
      </StyledSelectedIconDiv>
    </>
  );
}

function buildIconUrl(props: EntryPointSwitcherItemProps, iconName: string) {
  return `${props.iconBaseUrl}${iconName}.svg`;
}
